import { useCallback, useEffect, useRef, useState } from 'react';

import { LeadEntity } from '../../domain/entities/Lead';
import { useCases } from '../../infrastructure/di/container';

const LEADS_PER_PAGE = 10;
const PREFETCH_THRESHOLD = 3;
const MAX_SCAN_PAGES = 20;

export const useLeads = () => {
  const [leads, setLeads] = useState<LeadEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const listLeadsUseCaseRef = useRef(useCases.listLeads());
  const processLeadUseCaseRef = useRef(useCases.processLead());
  const listLeadsUseCase = listLeadsUseCaseRef.current;
  const processLeadUseCase = processLeadUseCaseRef.current;

  const requestVersionRef = useRef<number>(0);
  const seenIdsRef = useRef<Set<number>>(new Set());
  const visibleLeadsRef = useRef<LeadEntity[]>([]);
  const bufferRef = useRef<LeadEntity[]>([]);
  const isCurrentlyFetchingRef = useRef<boolean>(false);
  const hasMoreRef = useRef<boolean>(true);

  const replaceVisibleLeads = useCallback((nextVisibleLeads: LeadEntity[]) => {
    visibleLeadsRef.current = nextVisibleLeads;
    setLeads(nextVisibleLeads);
  }, []);

  const syncHasMoreState = useCallback(() => {
    setHasMore(hasMoreRef.current || bufferRef.current.length > 0 || isCurrentlyFetchingRef.current);
  }, []);

  const flushBufferToVisible = useCallback(() => {
    if (bufferRef.current.length === 0) {
      return false;
    }

    const nextVisibleLeads = [...visibleLeadsRef.current, ...bufferRef.current];
    bufferRef.current = [];
    replaceVisibleLeads(nextVisibleLeads);
    syncHasMoreState();

    return true;
  }, [replaceVisibleLeads, syncHasMoreState]);

  const loadNextBatch = useCallback(async (requestVersion: number): Promise<LeadEntity[]> => {
    if (requestVersion !== requestVersionRef.current || isCurrentlyFetchingRef.current || !hasMoreRef.current) {
      syncHasMoreState();

      return [];
    }

    isCurrentlyFetchingRef.current = true;
    syncHasMoreState();

    try {
      const nextBatch: LeadEntity[] = [];

      for (let page = 1; page <= MAX_SCAN_PAGES; page += 1) {
        const result = await listLeadsUseCase.execute({ page, perPage: LEADS_PER_PAGE });

        if (requestVersion !== requestVersionRef.current) {
          return [];
        }

        const freshLeads = result.items.filter((lead) => !seenIdsRef.current.has(lead.id));

        freshLeads.forEach((lead) => {
          seenIdsRef.current.add(lead.id);
        });

        nextBatch.push(...freshLeads);

        if (nextBatch.length >= LEADS_PER_PAGE) {
          hasMoreRef.current = true;

          return nextBatch;
        }

        if (result.items.length < LEADS_PER_PAGE) {
          if (nextBatch.length === 0 || result.items.length === 0) {
            hasMoreRef.current = false;
          }

          return nextBatch;
        }
      }
      hasMoreRef.current = false; // exhausted MAX_SCAN_PAGES without actionable fresh items
    } catch (error) {
      hasMoreRef.current = false;
      console.error('Failed to load leads', error);
    } finally {
      if (requestVersion === requestVersionRef.current) {
        isCurrentlyFetchingRef.current = false;
        syncHasMoreState();
      }
    }

    return [];
  }, [listLeadsUseCase, syncHasMoreState]);

  const fillBuffer = useCallback(async (requestVersion: number) => {
    if (requestVersion !== requestVersionRef.current || isCurrentlyFetchingRef.current || !hasMoreRef.current) {
      syncHasMoreState();

      return;
    }

    const nextBatch = await loadNextBatch(requestVersion);

    if (requestVersion !== requestVersionRef.current) {
      return;
    }

    if (visibleLeadsRef.current.length <= PREFETCH_THRESHOLD) {
      if (nextBatch.length > 0) {
        replaceVisibleLeads([...visibleLeadsRef.current, ...nextBatch]);
        syncHasMoreState();

        if (hasMoreRef.current && !isCurrentlyFetchingRef.current) {
          void fillBuffer(requestVersion);
        }

        return;
      }

      syncHasMoreState();

      return;
    }

    bufferRef.current = nextBatch;
    syncHasMoreState();
  }, [loadNextBatch, replaceVisibleLeads, syncHasMoreState]);

  const loadInitial = useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;

    requestVersionRef.current = requestVersion;
    setLoading(true);
    seenIdsRef.current = new Set<number>();
    isCurrentlyFetchingRef.current = false;
    visibleLeadsRef.current = [];
    bufferRef.current = [];
    hasMoreRef.current = true;
    replaceVisibleLeads([]);
    syncHasMoreState();

    const initialLeads = await loadNextBatch(requestVersion);

    if (requestVersion !== requestVersionRef.current) {
      return;
    }

    replaceVisibleLeads(initialLeads);
    setLoading(false);
    syncHasMoreState();

    if (hasMoreRef.current && !isCurrentlyFetchingRef.current) {
      void fillBuffer(requestVersion);
    }
  }, [fillBuffer, loadNextBatch, replaceVisibleLeads, syncHasMoreState]);

  const reload = useCallback(() => {
    void loadInitial();
  }, [loadInitial]);

  useEffect(() => {
    void loadInitial();

    return () => {
      requestVersionRef.current += 1;
    };
  }, [loadInitial]);

  const markProcessed = useCallback(
    (id: number, isTarget: boolean) => {
      const remaining = visibleLeadsRef.current.filter((lead) => lead.id !== id);
      replaceVisibleLeads(remaining);

      processLeadUseCase.execute(id, isTarget).catch((err) => {
        console.error('Failed to process lead', err);
      });

      const shouldTopUp = remaining.length <= PREFETCH_THRESHOLD;

      if (!shouldTopUp) {
        syncHasMoreState();

        return;
      }

      const flushed = flushBufferToVisible();

      if (flushed) {
        if (hasMoreRef.current && !isCurrentlyFetchingRef.current) {
          void fillBuffer(requestVersionRef.current);
        }

        return;
      }

      if (hasMoreRef.current && !isCurrentlyFetchingRef.current) {
        void fillBuffer(requestVersionRef.current);

        return;
      }

      syncHasMoreState();
    },
    [fillBuffer, flushBufferToVisible, processLeadUseCase, replaceVisibleLeads, syncHasMoreState]
  );

  return {
    leads,
    hasMore,
    loading,
    markProcessed,
    reload,
    isEmpty: !loading && leads.length === 0 && !hasMore && !isCurrentlyFetchingRef.current && bufferRef.current.length === 0,
  };
};
